import DestinationPicker from "../components/destinationPicker.jsx";
import DeparturePicker from "../components/departurePicker.jsx";
import React, { useState, useEffect } from "react";
import DatePicker from "../components/datePicker.jsx";
import PaxPicker from "../components/paxPicker.jsx";
import PackageDatasource from "../datasources/packageDatasource.js";
import PopoverInput from "../components/inner/popoverInput.jsx";
import useWindowDimensions from "../helpers/Hooks/useWindowDimensions";
import useJsonCookie from "../helpers/Hooks/useJsonCookie.jsx";
import { format, parse, differenceInDays } from "date-fns";

import { IsMobileContext, DataSourceContext } from '../context/contexts.jsx'
const DEFAULT_ROOMS = [{ adults: 2, children: [], infants: 0 }];
const serializeRooms = (rooms) => {

    let roomDefs = [];
    for (let i = 0; i < rooms.length; i++) {
        let myUrlSlug = "";
        const room = rooms[i];
        myUrlSlug = myUrlSlug + `A${room.adults}`;
        if (room.children.length > 0) {
            for (let c = 0; c < room.children.length; c++) {
                const childAge = room.children[c];
                myUrlSlug = myUrlSlug + `C${childAge}`;
            }
        }
        if (room.infants > 0) {
            myUrlSlug = myUrlSlug + `I${room.infants}`;
        }
        roomDefs.push(myUrlSlug);
    }
    return roomDefs.join('-');
}
const deserializeRooms = (roomsString) => {
    if (!roomsString) {
        return DEFAULT_ROOMS;
    }

    const roomDefs = roomsString.split('-');
    const rooms = [];

    for (let i = 0; i < roomDefs.length; i++) {
        const roomDef = roomDefs[i];
        const room = { adults: 0, children: [], infants: 0 };

        // Match pattern: A2C5C7I1 (adults, children with ages, infants)
        const adultsMatch = roomDef.match(/A(\d+)/);
        const childrenMatches = roomDef.matchAll(/C(\d+)/g);
        const infantsMatch = roomDef.match(/I(\d+)/);

        if (adultsMatch) {
            room.adults = parseInt(adultsMatch[1], 10);
        }

        for (const childMatch of childrenMatches) {
            room.children.push(parseInt(childMatch[1], 10));
        }

        if (infantsMatch) {
            room.infants = parseInt(infantsMatch[1], 10);
        }

        rooms.push(room);
    }

    return rooms;
}

const RedSeaSearch = ({ specificSearch, specificAccom, baseUrl, showForm }) => {
    if (specificSearch && specificAccom) {
        throw new Error("cannot do both");
    }

    // Separate cookies for each search type
    const [savedRoomsPackages, setSavedRoomsPackages, removeSavedRoomsPackages] = useJsonCookie('s_rooms_packages', DEFAULT_ROOMS, baseUrl);
    const [savedDeparturePackages, setSavedDeparturePackages, removeSavedDeparturePackages] = useJsonCookie("s_dep_packages", null, baseUrl);
    const [savedDestinationPackages, setSavedDestinationPackages, removeSavedDestinationPackages] = useJsonCookie("s_dest_packages", null, baseUrl);
    const [savedStartDatePackages, setSavedStartDatePackages, removeStartDatePackages] = useJsonCookie("s_from_date_packages", null, baseUrl);
    const [savedToDatePackages, setSavedToDatePackages, removeToDatePackages] = useJsonCookie("s_to_date_packages", null, baseUrl);

    const [savedRoomsCruises, setSavedRoomsCruises, removeSavedRoomsCruises] = useJsonCookie('s_rooms_cruises', DEFAULT_ROOMS, baseUrl);
    const [savedDepartureCruises, setSavedDepartureCruises, removeSavedDepartureCruises] = useJsonCookie("s_dep_cruises", null, baseUrl);
    const [savedDestinationCruises, setSavedDestinationCruises, removeSavedDestinationCruises] = useJsonCookie("s_dest_cruises", null, baseUrl);
    const [savedStartDateCruises, setSavedStartDateCruises, removeStartDateCruises] = useJsonCookie("s_from_date_cruises", null, baseUrl);
    const [savedToDateCruises, setSavedToDateCruises, removeToDateCruises] = useJsonCookie("s_to_date_cruises", null, baseUrl);

    const [savedRoomsCruiseAndStay, setSavedRoomsCruiseAndStay, removeSavedRoomsCruiseAndStay] = useJsonCookie('s_rooms_cruiseandstay', DEFAULT_ROOMS, baseUrl);
    const [savedDepartureCruiseAndStay, setSavedDepartureCruiseAndStay, removeSavedDepartureCruiseAndStay] = useJsonCookie("s_dep_cruiseandstay", null, baseUrl);
    const [savedDestinationCruiseAndStay, setSavedDestinationCruiseAndStay, removeSavedDestinationCruiseAndStay] = useJsonCookie("s_dest_cruiseandstay", null, baseUrl);
    const [savedStartDateCruiseAndStay, setSavedStartDateCruiseAndStay, removeStartDateCruiseAndStay] = useJsonCookie("s_from_date_cruiseandstay", null, baseUrl);
    const [savedToDateCruiseAndStay, setSavedToDateCruiseAndStay, removeToDateCruiseAndStay] = useJsonCookie("s_to_date_cruiseandstay", null, baseUrl);

    const [rooms, setRooms] = useState(DEFAULT_ROOMS);
    const [departures, setDepartures] = useState([]);
    const [destination, setDestination] = useState(null);
    const [selectedFromDate, setSelectedFromDate] = useState(null);
    const [selectedToDate, setSelectedToDate] = useState(null);
    const [overViewDepartureText, setOverViewDepartureText] = useState();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const { height, width } = useWindowDimensions();
    const isMobile = width < 1024;


    const [searchType, setSearchType] = useState(specificSearch?.type ?? specificAccom?.type ?? "packages");
    const [dataSource, setDataSource] = useState(PackageDatasource.getDataSource(specificSearch?.type ?? specificAccom?.type ?? "packages", baseUrl));

    // Helper function to get saved values for current search type
    const getSavedValues = (type) => {
        if (type === "cruises") {
            return {
                savedRooms: savedRoomsCruises,
                savedDeparture: savedDepartureCruises,
                savedDestination: savedDestinationCruises,
                savedStartDate: savedStartDateCruises,
                savedToDate: savedToDateCruises
            };
        } else if (type === "cruiseandstay") {
            return {
                savedRooms: savedRoomsCruiseAndStay,
                savedDeparture: savedDepartureCruiseAndStay,
                savedDestination: savedDestinationCruiseAndStay,
                savedStartDate: savedStartDateCruiseAndStay,
                savedToDate: savedToDateCruiseAndStay
            };
        } else {
            return {
                savedRooms: savedRoomsPackages,
                savedDeparture: savedDeparturePackages,
                savedDestination: savedDestinationPackages,
                savedStartDate: savedStartDatePackages,
                savedToDate: savedToDatePackages
            };
        }
    };

    // Helper function to set saved values for current search type
    const setSavedValues = (type, values) => {
        if (type === "cruises") {
            if (values.rooms !== undefined) setSavedRoomsCruises(values.rooms);
            if (values.departure !== undefined) setSavedDepartureCruises(values.departure);
            if (values.destination !== undefined) setSavedDestinationCruises(values.destination);
            if (values.startDate !== undefined) setSavedStartDateCruises(values.startDate);
            if (values.toDate !== undefined) setSavedToDateCruises(values.toDate);
        } else if (type === "cruiseandstay") {
            if (values.rooms !== undefined) setSavedRoomsCruiseAndStay(values.rooms);
            if (values.departure !== undefined) setSavedDepartureCruiseAndStay(values.departure);
            if (values.destination !== undefined) setSavedDestinationCruiseAndStay(values.destination);
            if (values.startDate !== undefined) setSavedStartDateCruiseAndStay(values.startDate);
            if (values.toDate !== undefined) setSavedToDateCruiseAndStay(values.toDate);
        } else {
            if (values.rooms !== undefined) setSavedRoomsPackages(values.rooms);
            if (values.departure !== undefined) setSavedDeparturePackages(values.departure);
            if (values.destination !== undefined) setSavedDestinationPackages(values.destination);
            if (values.startDate !== undefined) setSavedStartDatePackages(values.startDate);
            if (values.toDate !== undefined) setSavedToDatePackages(values.toDate);
        }
    };




    useEffect(() => {
        setDataSource(PackageDatasource.getDataSource(searchType, baseUrl));
        console.log("specificSearch", specificSearch);
        console.log("specificAccom", specificAccom);

        const saved = getSavedValues(searchType);
        var departureToUse = saved.savedDeparture;
        var destinationInUse = saved.savedDestination;
        var startDateToUse = saved.savedStartDate ? parse(saved.savedStartDate, "yyyy-MM-dd", new Date()) : null;
        var endDateToUse = saved.savedToDate ? parse(saved.savedToDate, "yyyy-MM-dd", new Date()) : null;
        var roomsToUse = saved.savedRooms;
        
        if (specificSearch) {
            departureToUse = specificSearch.dep;
            destinationInUse = specificSearch.to;
            if (specificSearch.startDate) {
                startDateToUse = parse(specificSearch.startDate, "yyyy-MM-dd", new Date());
            }
            if (specificSearch.endDate) {
                endDateToUse = parse(specificSearch.endDate, "yyyy-MM-dd", new Date());
            }
            if (specificSearch.pax) {
                roomsToUse = deserializeRooms(specificSearch.pax);
            }
        } else if (specificAccom) {

            departureToUse = specificAccom.dep;
            destinationInUse = { name: specificAccom.to.name, code: specificAccom.to.code };
            console.log("Setting dest from accom", destinationInUse);
            if (specificAccom.startDate) {
                startDateToUse = parse(specificAccom.startDate, "yyyy-MM-dd", new Date());
            }
            if (specificAccom.endDate) {
                endDateToUse = parse(specificAccom.endDate, "yyyy-MM-dd", new Date());
            }
            if (specificAccom.pax) {
                roomsToUse = deserializeRooms(specificAccom.pax);
            }
        }

        // Set rooms immediately for current search type
        setRooms(roomsToUse);

        async function fetchData() {
            var validateResponse = await dataSource.validate(departureToUse, destinationInUse?.code, startDateToUse, endDateToUse);

            if (validateResponse.isValidDeparture) {
                setDepartures(departureToUse);
            } else {
                setDepartures([]);
            }
            if (validateResponse.isValidDestination) {
                console.log("Seting dest to ", validateResponse.destinationModel);
                setDestination(validateResponse.destinationModel);
            } else {
                setDestination(null);
            }
            if (validateResponse.isValidDates) {
                setSelectedFromDate(startDateToUse);
                setSelectedToDate(endDateToUse)
            } else {
                setSelectedFromDate(null);
                setSelectedToDate(null);
            }
        }
        console.log("SearchType: ", searchType, { departureToUse, destinationInUse, startDateToUse, endDateToUse });
        if (departureToUse && departureToUse.length > 0 && destinationInUse && startDateToUse && endDateToUse) {
            fetchData();
        } else {
            // Clear form if no saved data
            setDepartures([]);
            setDestination(specificAccom ? { name: specificAccom.to.name, code: specificAccom.to.code } : null);
            setSelectedFromDate(null);
            setSelectedToDate(null);
        }
    }, [searchType]);

    useEffect(() => {

        async function fetchData() {
            var validateResponse = await dataSource.validate(departures, destination?.code, selectedFromDate, selectedToDate);

            if (validateResponse.isValidDeparture) {
                setDepartures(departures);
            } else {
                setDepartures(null);
            }
            if (validateResponse.isValidDestination) {
                console.log("Seting dest to ", validateResponse.destinationModel);
                setDestination(validateResponse.destinationModel);
            } else {
                console.log("Seting dest to wmpoty!!!!");

                setDestination(null);
            }
            if (validateResponse.isValidDates) {
                setSelectedFromDate(selectedFromDate);
                setSelectedToDate(selectedToDate)
            } else {
                setSelectedFromDate(null);
                setSelectedToDate(null)
            }
        }
        console.log("SearchType: ", searchType, { departures, destination, selectedFromDate, selectedToDate });
        if (departures && departures.length > 0 && destination && selectedFromDate && selectedToDate) {
            fetchData();
        }
    }, [searchType]);

    const submit = () => {
        if (!departures || departures.count == 0) {
            return;
        }
        if (!destination) {
            return;
        }
        if (!selectedFromDate || !selectedToDate) {
            return;
        }

        if (!selectedFromDate || !selectedToDate) {
            return;
        }

        const fromDate = format(selectedFromDate, "yyyy-MM-dd");
        const toDate = format(selectedToDate, "yyyy-MM-dd");
        
        // Save to the correct cookie set based on search type
        setSavedValues(searchType, {
            departure: departures,
            destination: !specificAccom ? destination : undefined,
            startDate: fromDate,
            toDate: toDate,
            rooms: rooms
        });

        var nights = differenceInDays(selectedToDate, selectedFromDate)
        let roomsString = serializeRooms(rooms);
        var dest = destination.code.replace("Hotel:","");
        let url = `/en/search/${searchType}/${departures.join('-')}/${dest}/${nights}/${fromDate}/${fromDate}/${roomsString}`;

        window.location.href = url;
    }


    window.toggleSearchForm = () => {
        setIsSearchOpen(!isSearchOpen);
    }
    console.log("Render", specificAccom, destination)
    var showSearch = showForm || (isSearchOpen || !isMobile);
    let selectedTabClass = "inline-flex items-center justify-center text-highlight p-4 text-fg-brand border-b border-highlight rounded-t-base active group";
    let unselectedTabClass = "inline-flex items-center justify-center p-4 border border-transparent rounded-t-base hover:text-highlight_dark hover:border-highlight_dark group";
    return (
        <IsMobileContext value={isMobile}>
            <DataSourceContext value={dataSource}>
                <div className={"relative " + (showSearch ? "" : "hidden")}>
                    <div className=" " >

                        {!specificAccom && (
                            <div class="border-b border-default">
                                <ul class="flex flex-wrap -mb-px text-sm text-white font-medium text-center">
                                    <li class="me-2">
                                        <a href="#" onClick={(e) => {
                                            e.preventDefault();
                                            setSearchType("packages");
                                        }} class={searchType === "packages" ? selectedTabClass : unselectedTabClass}>
                                            <svg class="w-4 h-4 me-2  " xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 864 864">
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M287.8,418.2h-63.4c-13.1,0-23.8,10.6-23.8,23.8s10.6,23.8,23.8,23.8h63.4c13.1,0,23.8-10.6,23.8-23.8s-10.6-23.8-23.8-23.8" />
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M400,323.7h63.7c13.2,0,23.9-10.6,24.1-23.8,0-6.4-2.5-12.5-7-17.1-4.5-4.5-10.6-7-17.1-7h-63.7c-13.2.2-23.8,10.9-23.8,24.1s10.6,23.8,23.8,23.8" />
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M224.4,323.7h63.4c13.2,0,23.9-10.6,24.1-23.8,0-6.4-2.5-12.5-7-17.1-4.5-4.5-10.6-7-17-7h-63.4c-13.3,0-24.1,10.8-24.1,24.1.2,13.2,10.9,23.8,24.1,23.8" />
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M463.8,418.2h-63.7c-13.1,0-23.8,10.6-23.8,23.8s10.6,23.8,23.8,23.8h63.7c13.1,0,23.8-10.6,23.8-23.8s-10.6-23.8-23.8-23.8" />
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M639.8,418.2h-63.7c-13.1,0-23.8,10.6-23.8,23.8s10.6,23.8,23.8,23.8h63.7c13.1,0,23.8-10.6,23.8-23.8s-10.6-23.8-23.8-23.8" />
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M576.2,323.7h63.7c13.1,0,23.8-10.6,23.8-23.8s-10.6-23.9-23.8-24.1h-63.7c-13.3,0-24.1,10.8-24.1,24.1.1,13.2,10.9,23.8,24.1,23.8" />
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M327,664.6c0-10.6,4.2-20.7,11.7-28.1,7.5-7.4,17.7-11.6,28.2-11.5h127.4c10.6,0,20.7,4.1,28.2,11.5,7.5,7.4,11.7,17.5,11.7,28.1v155.4h-207.3v-155.4ZM116.7,197c0-10.4,4.3-20.4,11.8-27.7,7.1-6.9,16.7-10.7,26.6-10.9.6,0,1.1-.3,1.7-.2h550.5c.6,0,1.1.2,1.7.2,9.9.2,19.5,3.9,26.6,10.9,7.4,7.3,11.7,17.2,11.8,27.7v385.6s2,237.4,2,237.4h-165.8v-154.8c0-48.7-39.4-88.1-88-88.1h-127.5c-23.2,0-45.4,9.2-61.9,25.5-16.5,16.5-25.7,38.7-25.8,61.9v155.4c0,0-165.8,0-165.8,0l2.1-237.4V197ZM355.9,48h256.2c10.9-.4,21.5,3.8,29.3,11.4,7.8,7.8,12.1,18.4,12.1,29.4v20.7H210.6v-20.7c0-11,4.3-21.6,12-29.4,7.8-7.7,18.4-11.8,29.4-11.4M842,820h-46.8v-237.4s0-385.1,0-385.1c0-1.1-.3-2.2-.3-3.3-.4-22-8.8-43.2-24.1-59.3-16.5-16.3-38.7-25.5-61.9-25.5h-7.9v-20.7c0-23.3-9.4-45.5-25.8-61.9-16.7-16.8-39.4-26.3-63.1-26.1H252c-23.7-.1-46.4,9.3-63.1,26.1-16.5,16.4-25.7,38.7-25.8,61.9v20.7h-7.9c-23.2,0-45.4,9.2-61.9,25.5-15.3,16-23.7,37.2-24.1,59.3,0,1.1-.3,2.2-.3,3.3v622.4s-46.8,0-46.8,0c-12.2,0-22,9.8-22,22s9.8,22,22,22h820.1c12.2,0,22-9.8,22-22s-9.8-22-22-22" />
                                            </svg> Holidays
                                        </a>
                                    </li>
                                    <li class="me-2">
                                        <a href="#" onClick={(e) => {
                                            e.preventDefault();
                                            setSearchType("cruises");
                                        }} class={searchType === "cruises" ? selectedTabClass : unselectedTabClass} aria-current="page">
                                            <svg class="w-4 h-4 me-2 " xmlns="http://www.w3.org/2000/svg" viewBox="0 0 864 864">
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M755.7,863.5c-32.5,0-61.6-14.9-80.7-38.3-19.1,23.4-48.2,38.3-80.7,38.3s-61.6-14.9-80.7-38.3c-19.1,23.4-48.2,38.3-80.7,38.3s-61.6-14.9-80.7-38.3c-19.1,23.4-48.2,38.3-80.7,38.3s-61.8-15-80.9-38.6c-19.5,23.5-49.1,38.6-82.3,38.6-57.5,0-104.3-46.8-104.3-104.3h47.1c0,31.5,25.6,57.1,57.1,57.1s59-26.2,59-57.1h47.1c0,31.5,25.6,57.1,57.1,57.1s57.1-25.6,57.1-57.1h47.1c0,31.5,25.6,57.1,57.1,57.1s57.1-25.6,57.1-57.1h47.1c0,31.5,25.6,57.1,57.1,57.1s57.1-25.6,57.1-57.1h47.1c0,31.5,25.6,57.1,57.1,57.1s57.1-25.6,57.1-57.1h47.1c0,57.5-46.8,104.3-104.3,104.3Z" />
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M432,862.1" />
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M166.7,826.2c-67.7-58.5-114.8-138.7-132.5-225.9l-5.8-28.3h807.3l-5.8,28.3c-17.2,84.5-62.2,162.8-126.7,220.7l-31.5-35.1c49.8-44.7,86.4-103.1,105-166.8H87.3c19.3,66,57.7,126,110.2,171.3l-30.8,35.7Z" />
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M774.9,525.3h-423.6V.5l423.6,524.8ZM398.4,478.2h277.9L398.4,133.9v344.3Z" />
                                                <path stroke="currentColor" fill="currentColor" stroke-linecap="round" stroke-width="2" d="M398.5,525.3H107.4L398.5,164.7v360.6ZM206,478.2h145.4v-180.1l-145.4,180.1Z" />
                                            </svg> Cruises
                                        </a>
                                    </li>
                                    <li class="me-2">
                                        <a href="#" onClick={(e) => {
                                            e.preventDefault();
                                            setSearchType("cruiseandstay");
                                        }} class={searchType === "cruiseandstay" ? selectedTabClass : unselectedTabClass}>
                                            Cruise and Stay
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        )}
                        <div className="flex flex-col lg:flex-row items-center justify-center gap-4 px-2 lg:px-0 lg:py-2">
                            {/* Departure Dropdown */}
                            <DeparturePicker
                                selectedAirports={departures}

                                updateOverviewText={setOverViewDepartureText}
                                onUpdate={setDepartures}
                            />
                            <DestinationPicker
                                departureAirports={departures}

                                accomIsLocked={!!specificAccom}
                                selectedDestination={destination}
                                onUpdate={setDestination}
                            />
                            <DatePicker
                                departureAirports={departures}
                                destination={destination?.code}

                                selectedFrom={selectedFromDate}
                                selectedTo={selectedToDate}
                                onChange={(from, to) => {
                                    setSelectedFromDate(from);
                                    setSelectedToDate(to);
                                }}
                            />
                            <PaxPicker
                                rooms={rooms}
                                onChange={setRooms}
                            />

                            <div className="w-full lg:w-[150px] p-2 lg:p-0">
                                <button onClick={() => submit()} type="submit" className=" p-4 btn-pri w-full lg:w-[150px] lg:p-2">
                                    Search
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </DataSourceContext>
        </IsMobileContext>);
};
export default RedSeaSearch;
